import type { Company } from "@dutyon/apis";
import { createNotice, getCompanies, mapClientError, queryKeys } from "@dutyon/apis";
import { Card, notifications, TitleContainer } from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { NoticeForm, type NoticeFormPayload } from "../../components/NoticeForm";

interface Props {
  target: "APP" | "DASHBOARD";
}

export function NoticeCreatePage({ target }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const targetLabel = target === "APP" ? "앱" : "대시보드";
  const listPath = target === "APP" ? "/notices/app" : "/notices/dashboard";

  // 병원 목록 조회 (공지 연결용)
  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: queryKeys.companies.all,
    queryFn: async () => {
      const list = await getCompanies();
      return list;
    },
  });

  const createMutation = useMutation({
    mutationFn: createNotice,
    onSuccess: () => {
      notifications.show({
        title: "성공",
        message: "공지사항이 등록되었습니다.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.notices.all });
      navigate(listPath);
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "실패",
        message: mapClientError(error, {
          fallback: "공지사항 등록 중 오류가 발생했습니다.",
        }).message,
        color: "red",
      });
    },
  });

  const handleSubmit = (payload: NoticeFormPayload) => {
    createMutation.mutate(payload);
  };

  return (
    <TitleContainer size="md" title={`${targetLabel} 공지사항 등록`}>
      <Card withBorder p="xl">
        <NoticeForm
          target={target}
          companies={companies}
          onSubmit={handleSubmit}
          loading={createMutation.isPending}
          onCancel={() => navigate(listPath)}
        />
      </Card>
    </TitleContainer>
  );
}
